import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  addFlow45_1LCA,
  deleteFlow45_1LCA,
  getAllFlow45_1LCA,
  updateFlow45_1LCA,
} from "../../../../../../apis/Product/DisposalFlow45Api";
import { getProductFlow45_1DisposedDropdown } from "../../../../../../apis/Dropdown";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

// Waste process scenario dropdown options
const WASTE_PROCESS_SCENARIO_OPTIONS = [
  { value: "waste treatment, co-firing power plant elastomers", label: "waste treatment, co-firing power plant elastomers" },
  { value: "waste treatment, co-firing power plant Other plastics", label: "waste treatment, co-firing power plant Other plastics" },
  { value: "waste treatment, combustion in small elec. power plant", label: "waste treatment, combustion in small elec. power plant" },
  { value: "waste treatment, municipal waste incineration with electricity, elastomers", label: "waste treatment, municipal waste incineration with electricity, elastomers" },
  { value: "waste treatment, municipal waste incineration with electricity, others", label: "waste treatment, municipal waste incineration with electricity, others" },
  { value: "waste treatment, municipal waste incineration with electricity, thermoplastics", label: "waste treatment, municipal waste incineration with electricity, thermoplastics" },
  { value: "waste treatment, municipal waste incineration with electricity, thermosets", label: "waste treatment, municipal waste incineration with electricity, thermosets" },
  { value: "waste treatment, municipal waste incineration with electricity, Other plastics", label: "waste treatment, municipal waste incineration with electricity, Other plastics" },
  { value: "waste treatment, municipal waste incineration with electricity, Additives", label: "waste treatment, municipal waste incineration with electricity, Additives" },
  { value: "no heat recovery, waste combustion, elastomeres", label: "no heat recovery, waste combustion, elastomeres" },
  { value: "no heat recovery, waste combustion, others", label: "no heat recovery, waste combustion, others" },
  { value: "no heat recovery, waste combustion, thermoplasts", label: "no heat recovery, waste combustion, thermoplasts" },
  { value: "no heat recovery, waste combustion, thermosets", label: "no heat recovery, waste combustion, thermosets" },
  { value: "no heat recovery, waste combustion, Other plastics", label: "no heat recovery, waste combustion, Other plastics" },
  { value: "no heat recovery, waste combustion, Additives", label: "no heat recovery, waste combustion, Additives" },
  { value: "waste treatment, packaging end-of-life (municipal waste incineration)", label: "waste treatment, packaging end-of-life (municipal waste incineration)" },
  { value: "waste treatment, recycling credit metals (note: can only be applied when the a market mix is used at the input, not for secondary metals at the input, since that would cause double counting)", label: "waste treatment, recycling credit metals (note: can only be applied when the a market mix is used at the input, not for secondary metals at the input, since that would cause double counting)" },
  { value: "waste treatment, upcycling credit plastics", label: "waste treatment, upcycling credit plastics" },
  { value: "waste treatment, waste handling, collection & sorting", label: "waste treatment, waste handling, collection & sorting" },
];

const Flow45_1Table = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [disposedDropdown, setDisposedDropdown] = useState([]);
  const [unitDisposed, setUnitDisposed] = useState({});

  // Fetch dropdown
  useEffect(() => {
    getProductFlow45_1DisposedDropdown({
      setDropdown: setDisposedDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllFlow45_1LCA({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && disposedDropdown.length > 0) {
      const newUnits = {};
      data.forEach((row) => {
        const disposedCode =
          typeof row.how_is_part_disposed === "string"
            ? row.how_is_part_disposed
            : row.how_is_part_disposed?.value || row.how_is_part_disposed?.code;
        if (disposedCode) {
          const selected = disposedDropdown.find(
            (opt) =>
              !opt.isCategory &&
              (opt.code === disposedCode ||
                opt.value === disposedCode ||
                opt.name === disposedCode ||
                String(opt.value) === String(disposedCode))
          );
          if (selected) {
            newUnits[row.id] = selected.unit || "xx";
          }
        }
      });
      setUnitDisposed(newUnits);
    }
  }, [data, disposedDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      how_is_part_disposed: {
        targetField: "quantity_per_fu",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          _rowData,
          rowId = "new",
          fullOption
        ) => {
          if (
            fullOption &&
            typeof fullOption === "object" &&
            fullOption.unit != null &&
            fullOption.unit !== ""
          ) {
            setUnitDisposed((prev) => ({
              ...prev,
              [rowId]: fullOption.unit,
            }));
            return;
          }
          const selected = dropdownData?.find(
            (opt) =>
              !opt.isCategory &&
              (opt?.value === selectedValue ||
                opt?.code === selectedValue ||
                opt?.name === selectedValue ||
                String(opt?.value) === String(selectedValue))
          );
          if (selected) {
            setUnitDisposed((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
          }
        },
        dropdownDataKey: "how_is_part_disposed",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const disposedCode =
        typeof editedItem.how_is_part_disposed === "string"
          ? editedItem.how_is_part_disposed
          : editedItem.how_is_part_disposed?.value || 
            editedItem.how_is_part_disposed?.code || 
            null;

      const wasteProcessScenario =
        typeof editedItem.waste_process_scenario === "string"
          ? editedItem.waste_process_scenario
          : editedItem.waste_process_scenario?.value || 
            editedItem.waste_process_scenario?.code || 
            null;

      updateFlow45_1LCA({
        productId,
        rowId: editedItem.id,
        name_part_in_fu: editedItem.name_part_in_fu || null,
        describe_process: editedItem.describe_process || null,
        how_is_part_disposed: disposedCode,
        quantity_per_fu: editedItem.quantity_per_fu || 0,
        waste_process_scenario: wasteProcessScenario,
        collecting_scenario: editedItem.collecting_scenario || null,
        surplus_material_replaces_new_purchase: editedItem.surplus_material_replaces_new_purchase || false,
        objects: data,
        setObjects: setData,
        use_swal: true,
        callback: () => {
          window.dispatchEvent(new Event('refresh-disposal-45-totals'));
        },
      });
    },
    [productId, data]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const disposedCode =
        typeof newItem.how_is_part_disposed === "string"
          ? newItem.how_is_part_disposed
          : newItem.how_is_part_disposed?.value || 
            newItem.how_is_part_disposed?.code || 
            null;

      const wasteProcessScenario =
        typeof newItem.waste_process_scenario === "string"
          ? newItem.waste_process_scenario
          : newItem.waste_process_scenario?.value || 
            newItem.waste_process_scenario?.code || 
            null;

      addFlow45_1LCA({
        productId,
        name_part_in_fu: newItem.name_part_in_fu || null,
        describe_process: newItem.describe_process || null,
        how_is_part_disposed: disposedCode,
        quantity_per_fu: newItem.quantity_per_fu || 0,
        waste_process_scenario: wasteProcessScenario,
        collecting_scenario: newItem.collecting_scenario || null,
        surplus_material_replaces_new_purchase: newItem.surplus_material_replaces_new_purchase || false,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-disposal-45-totals'));
        },
        use_swal: true,
      });
    },
    [productId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteFlow45_1LCA({
        productId,
        rowId: itemToDelete.id,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-disposal-45-totals'));
        },
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "name_part_in_fu",
        accessor: "name_part_in_fu",
        header: t("NAME_PART_IN_FU") || "Name the part in the FU *",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: "describe_process",
        accessor: "describe_process",
        header: t("DESCRIBE_PROCESS") || "Describe the process",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "how_is_part_disposed",
        accessor: "how_is_part_disposed",
        header: `${t("HOW_IS_PART_DISPOSED") || "How is the part disposed"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_DISPOSAL_METHOD") || "Select disposal method...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "how_is_part_disposed",
            disposedDropdown,
            unitDisposed,
            rowId
          ),
      },
      {
        id: "quantity_per_fu",
        accessor: "quantity_per_fu",
        header: `${t("QUANTITY_PER_FU") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "how_is_part_disposed",
            disposedDropdown,
            unitDisposed,
            rowId
          ),
      },
      {
        id: "waste_process_scenario",
        accessor: "waste_process_scenario",
        header: `${t("WASTE_PROCESS_SCENARIO") || "Waste process scenario"} *`,
        type: "dropdown",
        placeholder: t("SELECT_WASTE_PROCESS_SCENARIO") || "Select waste process scenario...",
      },
      {
        id: "collecting_scenario",
        accessor: "collecting_scenario",
        header: t("COLLECTING_SCENARIO") || "Collecting scenario",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "surplus_material_replaces_new_purchase",
        accessor: "surplus_material_replaces_new_purchase",
        header: t("SURPLUS_MATERIAL_REPLACES_NEW_PURCHASE") || "Does the surplus material replace a new purchase?",
        type: "checkbox",
        optional: true,
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitDisposed,
      disposedDropdown,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      how_is_part_disposed: disposedDropdown || [],
      waste_process_scenario: WASTE_PROCESS_SCENARIO_OPTIONS || [],
    }),
    [disposedDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({
          [impactCategoryAccessor]: CustomInputComponent,
        }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default Flow45_1Table;

